import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Pill, Flame, Sparkles, Dumbbell, Brain, Shield, Heart, Zap, Clock, TrendingUp, ChevronDown, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Phase 2 — fuzzy match the catalog against the static peptide list. The
// catalog has variant names like "Semaglutide 12.5mg" while this section uses
// canonical names like "Semaglutide" — we match by best prefix.
function findPriceForPeptide(catalog, name) {
  if (!catalog?.length) return null;
  const lc = name.toLowerCase();
  // Exact name match wins.
  let hit = catalog.find((p) => p.name.toLowerCase() === lc);
  // Then prefix match (so "Semaglutide" picks up "Semaglutide 12.5mg").
  if (!hit) hit = catalog.find((p) => p.name.toLowerCase().startsWith(lc));
  // Then "contains" — catches "CJC-1295 / Ipamorelin" → "CJC-1295".
  if (!hit) {
    const firstWord = lc.split(/[\s\/]/)[0];
    if (firstWord.length >= 4) hit = catalog.find((p) => p.name.toLowerCase().includes(firstWord));
  }
  return hit || null;
}

const peptides = [
  {
    name: "Semaglutide", category: "Weight Management", icon: Flame,
    color: "bg-emerald-50 border-emerald-200", iconColor: "text-emerald-600",
    description: "A GLP-1 receptor agonist that regulates appetite and blood sugar. FDA-recognized and clinically proven to reduce body weight by 15-20% when combined with lifestyle changes.",
    howItWorks: "Mimics the GLP-1 hormone that targets areas of the brain that regulate appetite and food intake. It slows gastric emptying so you feel full longer, and improves insulin sensitivity.",
    benefits: ["Significant appetite suppression", "Steady, sustainable fat loss", "Improved blood sugar levels", "Reduced cardiovascular risk", "Lower inflammation markers"],
    dosing: "Weekly subcutaneous injection. Start at low dose and titrate up over 4-8 weeks to minimize side effects.",
    timeline: "Appetite changes in 1-2 weeks. Visible weight loss within 4-8 weeks. Full results at 3-6 months.",
  },
  {
    name: "Tirzepatide", category: "Weight Management", icon: Flame,
    color: "bg-emerald-50 border-emerald-200", iconColor: "text-emerald-600",
    description: "A dual GIP/GLP-1 receptor agonist — the most advanced weight loss peptide available. Works on two hormonal pathways simultaneously for enhanced results compared to semaglutide alone.",
    howItWorks: "Activates both GIP and GLP-1 receptors, providing a synergistic effect on appetite suppression, insulin sensitivity, and fat metabolism that neither pathway achieves alone.",
    benefits: ["Accelerated fat loss (up to 22% body weight)", "Improved insulin sensitivity", "Reduced visceral fat", "Sustained energy", "Potential cardiovascular benefits"],
    dosing: "Weekly subcutaneous injection. Gradual dose escalation over several weeks.",
    timeline: "Appetite suppression within days. Measurable weight loss in 4 weeks. Transformative results at 3-6 months.",
  },
  {
    name: "BPC-157", category: "Recovery & Healing", icon: Zap,
    color: "bg-blue-50 border-blue-200", iconColor: "text-blue-600",
    description: "Body Protection Compound-157, derived from a protein found in gastric juice. One of the most versatile healing peptides, accelerating tissue repair throughout the entire body.",
    howItWorks: "Promotes angiogenesis (new blood vessel formation), upregulates growth factor expression, and modulates nitric oxide production to accelerate healing of muscles, tendons, ligaments, and gut lining.",
    benefits: ["Rapid tissue repair", "Reduced joint and muscle pain", "Gut healing and protection", "Anti-inflammatory effects", "Tendon and ligament recovery"],
    dosing: "Daily subcutaneous injection near the injury site or systemically. Typical cycle: 4-12 weeks.",
    timeline: "Pain relief in 1-2 weeks. Significant healing at 4-6 weeks. Full tissue repair at 8-12 weeks.",
  },
  {
    name: "TB-500", category: "Recovery & Performance", icon: Dumbbell,
    color: "bg-blue-50 border-blue-200", iconColor: "text-blue-600",
    description: "Thymosin Beta-4, a naturally occurring peptide that plays a crucial role in tissue repair and regeneration. Excellent for athletic recovery and injury healing.",
    howItWorks: "Promotes new blood vessel growth, muscle fiber repair, and cell migration to injury sites. Also reduces inflammation and promotes stem cell maturation.",
    benefits: ["Enhanced muscle recovery", "Reduced stiffness and soreness", "Improved flexibility", "Accelerated wound healing", "Reduced scar tissue formation"],
    dosing: "Twice weekly subcutaneous injection. Loading phase for 4-6 weeks, then maintenance.",
    timeline: "Reduced soreness in 1-2 weeks. Notable recovery at 4 weeks. Peak benefits at 8-12 weeks.",
  },
  {
    name: "GHK-Cu", category: "Anti-Aging & Skin", icon: Sparkles,
    color: "bg-purple-50 border-purple-200", iconColor: "text-purple-600",
    description: "A copper peptide that is the gold standard in regenerative skincare. Stimulates collagen and elastin production, tightens loose skin, reduces fine lines, and even promotes hair growth.",
    howItWorks: "Activates multiple regenerative pathways: stimulates collagen synthesis, attracts immune cells for repair, increases antioxidant enzymes, and promotes new blood vessel growth in the skin.",
    benefits: ["Firmer, tighter skin", "Reduced wrinkles and fine lines", "Improved skin texture and tone", "Thicker, healthier hair", "Faster wound healing"],
    dosing: "Daily subcutaneous injection or topical application. Can also be used as a cream/serum.",
    timeline: "Skin texture changes in 2-4 weeks. Visible anti-aging results at 6-8 weeks. Cumulative improvements over 3-6 months.",
  },
  {
    name: "CJC-1295 / Ipamorelin", category: "Growth Hormone", icon: TrendingUp,
    color: "bg-amber-50 border-amber-200", iconColor: "text-amber-600",
    description: "A powerful growth hormone releasing peptide stack that naturally boosts your GH levels. Improves body composition, sleep, recovery, and overall vitality without the risks of synthetic HGH.",
    howItWorks: "CJC-1295 extends the half-life of growth hormone releasing hormone (GHRH), while Ipamorelin selectively stimulates GH release without affecting cortisol or prolactin.",
    benefits: ["Better deep sleep", "Increased lean muscle mass", "Reduced body fat", "Faster recovery from workouts", "Improved skin elasticity", "Enhanced cognitive function"],
    dosing: "Daily subcutaneous injection, ideally before bedtime to align with natural GH release patterns.",
    timeline: "Sleep improvements in 1-2 weeks. Body composition changes at 4-8 weeks. Full optimization at 3-6 months.",
  },
  {
    name: "Thymosin Alpha-1", category: "Immune Defense", icon: Shield,
    color: "bg-teal-50 border-teal-200", iconColor: "text-teal-600",
    description: "A powerful immune-modulating peptide used by physicians worldwide. Enhances T-cell function, fights chronic infections, and reduces autoimmune flare-ups. FDA-approved in over 30 countries.",
    howItWorks: "Stimulates the maturation and activation of T-cells, dendritic cells, and natural killer cells. Modulates the immune response to reduce chronic inflammation while enhancing pathogen defense.",
    benefits: ["Stronger immune response", "Fewer infections", "Reduced autoimmune symptoms", "Better resilience to illness", "Anti-inflammatory effects"],
    dosing: "Twice weekly subcutaneous injection. Ongoing maintenance protocol for chronic conditions.",
    timeline: "Immune support begins within days. Measurable immune markers improve at 4 weeks. Full benefits at 2-3 months.",
  },
  {
    name: "Semax", category: "Cognitive Enhancement", icon: Brain,
    color: "bg-pink-50 border-pink-200", iconColor: "text-pink-600",
    description: "A neuropeptide that boosts BDNF (brain-derived neurotrophic factor). Enhances focus, memory, and mental clarity while reducing anxiety — without sedation or stimulant side effects.",
    howItWorks: "Increases BDNF levels in the brain, promoting neuroplasticity, new neural connections, and enhanced cognitive function. Also modulates serotonin and dopamine for mood improvement.",
    benefits: ["Sharper focus and concentration", "Improved working memory", "Reduced brain fog", "Lower anxiety without sedation", "Enhanced motivation and drive"],
    dosing: "Daily intranasal spray. 1-2 sprays per nostril, typically in the morning.",
    timeline: "Mental clarity improvements within days. Significant cognitive gains at 2-4 weeks. Peak performance at 6-8 weeks.",
  },
  {
    name: "PT-141 (Bremelanotide)", category: "Sexual Wellness", icon: Heart,
    color: "bg-rose-50 border-rose-200", iconColor: "text-rose-600",
    description: "Works directly on the central nervous system to enhance sexual desire and performance in both men and women. Targets the root cause — your brain chemistry, not blood flow.",
    howItWorks: "Activates melanocortin receptors in the brain that control sexual arousal and desire. A fundamentally different mechanism than PDE5 inhibitors, addressing desire itself.",
    benefits: ["Increased natural desire", "Improved arousal response", "Enhanced sexual performance", "Works for both men and women", "Addresses the psychological component"],
    dosing: "Subcutaneous injection as needed, 1-2 hours before activity. Not for daily use.",
    timeline: "Effects noticeable within 1-2 hours of administration. Consistent improvements with regular use over 4-8 weeks.",
  },
  {
    name: "Epithalon", category: "Longevity", icon: Sparkles,
    color: "bg-purple-50 border-purple-200", iconColor: "text-purple-600",
    description: "A telomerase-activating peptide that lengthens telomeres — the biological clock of your cells. One of the most researched longevity peptides, studied for over 35 years.",
    howItWorks: "Activates telomerase, the enzyme that rebuilds telomeres (protective caps on chromosomes that shorten with age). Longer telomeres = slower biological aging at the cellular level.",
    benefits: ["Improved sleep quality", "Enhanced immune function", "Slowed biological aging", "Increased cellular vitality", "Potential cancer-protective effects"],
    dosing: "10-day injection cycle, repeated 2-3 times per year. Subcutaneous injection.",
    timeline: "Better sleep within 1-2 weeks. Energy improvements at 4 weeks. Measurable telomere changes over 3-6 months.",
  },
];

export default function PeptidesSection() {
  const [expanded, setExpanded] = useState(null);
  const [catalog, setCatalog] = useState([]);

  // Fetch the public price catalog once. Skip silently on error — the page
  // should still render with descriptions even if pricing is unavailable.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/public/peptide-pricing`)
      .then((r) => {
        if (!cancelled) setCatalog(r.data?.peptides || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const categories = [...new Set(peptides.map((p) => p.category))];

  return (
    <section id="peptides" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-14">
          <span className="text-xs font-sans font-medium uppercase tracking-[0.2em] text-[#C47C5D] mb-3 block">
            Our Protocols
          </span>
          <h2 className="text-3xl md:text-4xl font-serif font-light tracking-tight text-[#2A3324] mb-3">
            Peptide Recovery Guide
          </h2>
          <p className="text-base font-sans font-light text-[#2A3324]/50 max-w-2xl mx-auto">
            Explore our full range of physician-guided peptide protocols. Click any peptide to learn how it works, dosing, benefits, and timeline.
          </p>
        </div>

        <div className="space-y-3 max-w-4xl mx-auto">
          {peptides.map((p, i) => {
            const isOpen = expanded === i;
            const Icon = p.icon;
            const priceRow = findPriceForPeptide(catalog, p.name);
            return (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.03 }}
                className={`bg-[#F7F5F0] rounded-2xl border ${isOpen ? "border-[#5C715E]/30 shadow-lg bg-white" : "border-[#2A3324]/5"} overflow-hidden transition-all duration-300`}
              >
                <button
                  data-testid={`peptide-card-${i}`}
                  onClick={() => setExpanded(isOpen ? null : i)}
                  className="w-full flex items-center gap-4 p-5 md:p-6 text-left"
                >
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${p.color}`}>
                    <Icon size={22} strokeWidth={1.5} className={p.iconColor} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-lg font-serif font-normal text-[#2A3324]">{p.name}</p>
                    <p className="text-xs font-sans text-[#2A3324]/40">{p.category}</p>
                  </div>
                  {priceRow && (
                    <div data-testid={`peptide-price-${i}`} className="text-right shrink-0 hidden sm:block">
                      <p className="text-base md:text-lg font-serif font-medium text-[#2A3324]">
                        From ${Math.round(priceRow.display_price_monthly)}<span className="text-xs font-sans font-light text-[#2A3324]/50">/mo</span>
                      </p>
                      {priceRow.is_glp1 && (
                        <p className="text-[10px] font-sans uppercase tracking-wider text-[#C47C5D]">Program fee</p>
                      )}
                    </div>
                  )}
                  <ChevronDown size={20} className={`text-[#2A3324]/30 transition-transform duration-300 shrink-0 ${isOpen ? "rotate-180" : ""}`} />
                </button>

                {isOpen && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="px-5 md:px-6 pb-6"
                  >
                    <p className="text-sm font-sans font-light leading-relaxed text-[#2A3324]/70 mb-6">
                      {p.description}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="bg-[#F7F5F0] rounded-xl p-5">
                        <h4 className="text-xs font-sans font-semibold uppercase tracking-wider text-[#5C715E] mb-2">How It Works</h4>
                        <p className="text-sm font-sans font-light text-[#2A3324]/60 leading-relaxed">{p.howItWorks}</p>
                      </div>
                      <div className="bg-[#F7F5F0] rounded-xl p-5">
                        <h4 className="text-xs font-sans font-semibold uppercase tracking-wider text-[#C47C5D] mb-2">Dosing Protocol</h4>
                        <p className="text-sm font-sans font-light text-[#2A3324]/60 leading-relaxed">{p.dosing}</p>
                      </div>
                    </div>

                    <div className="mb-6">
                      <h4 className="text-xs font-sans font-semibold uppercase tracking-wider text-[#2A3324]/40 mb-3">Key Benefits</h4>
                      <div className="flex flex-wrap gap-2">
                        {p.benefits.map((b) => (
                          <span key={b} className="text-xs font-sans font-medium px-3 py-1.5 rounded-full bg-[#5C715E]/10 text-[#5C715E]">{b}</span>
                        ))}
                      </div>
                    </div>

                    <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl mb-4">
                      <Clock size={16} className="text-blue-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs font-sans font-semibold text-blue-700 mb-1">Expected Timeline</p>
                        <p className="text-xs font-sans text-blue-600/70">{p.timeline}</p>
                      </div>
                    </div>

                    <a href="#quiz" className="inline-flex items-center gap-2 text-sm font-sans font-medium text-[#C47C5D] hover:text-[#B36B4C] transition-colors">
                      See if {p.name} is right for you <ArrowRight size={14} />
                    </a>
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
